import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import { getQueuedChatClientIds } from "web_myplann/Chat/Helpers/getQueuedChatClientIds";
import { getUnsentChatMessages } from "web_myplann/Chat/Helpers/getUnsentChatMessages";
import { mapChatMessage } from "web_myplann/Chat/Helpers/mapChatMessage";
import type { ChatMessageView } from "web_myplann/Chat/Types/chatMessage";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";
import { postChatDetailList } from "web_myplann/Infrastructure/ApiOld/Api";

export type ChatMessagesPage = {
	items: ChatMessageView[];
	isNextPage: boolean;
};

const FIRST_PAGE = 1;

const fetchChatMessages = processResponse(postChatDetailList);

export const useChatMessagesQuery = (conversationId: number | null) => {
	const queryClient = useQueryClient();

	return useInfiniteQuery({
		queryKey: ChatKeys.messages(conversationId),
		initialPageParam: FIRST_PAGE,
		queryFn: async ({ pageParam }): Promise<ChatMessagesPage> => {
			const response = await fetchChatMessages({
				id: conversationId!,
				page: pageParam,
			});
			const data = response?.data;
			const items = data?.items.map(mapChatMessage) ?? [];

			if (pageParam !== FIRST_PAGE) {
				return { items, isNextPage: data?.isNextPage ?? false };
			}

			const unsent = getUnsentChatMessages(
				queryClient,
				conversationId,
				getQueuedChatClientIds(queryClient, conversationId),
			);

			return {
				items: [...items, ...unsent],
				isNextPage: data?.isNextPage ?? false,
			};
		},
		enabled: conversationId !== null,
		getNextPageParam: (lastPage, allPages) =>
			lastPage.isNextPage ? allPages.length + 1 : undefined,
		select: (data) => data.pages.flatMap((page) => page.items),
		meta: { persist: false },
	});
};
